import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { Icon } from "~/features/ui/icon";
import { Link } from "~/features/ui/link";
import { cleanStegaForTextTransform } from "~/features/sanity/stega";
import type { NavDropdownCategory } from "~/features/app/app-header-v2/nav-links/nav-dropdown-types";
import CaretRight from "~/public/icons/caret-right.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const paragraph = /*#__PURE__*/_css(["& > div{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}"]);
type Props = {
  category: NavDropdownCategory;
  isSelected: boolean;
  isActive?: boolean;
  onSelect: (id: string) => void;
  onNavigate: () => void;
  /** Light panel (e.g. page highlight): dark text on white megamenu. */
  surface?: "dark" | "light";
};
function extractTitleAndDescription(rawText: string): {
  title: string;
  description: string | null;
} {
  // Cleaned because the output is split into per-line spans by GSAP SplitText, which fractures the
  // appended stega payload (it contains a zero-width space) into malformed fragments.
  const text = cleanStegaForTextTransform(rawText);
  const match = text.match(/^(.*?)\s*\[(.*?)\]\s*$/);
  if (match && match[1] !== undefined && match[2] !== undefined) {
    return {
      title: match[1].trim(),
      description: match[2].trim()
    };
  }
  return {
    title: text,
    description: null
  };
}
export function CategoryLink({
  category,
  isSelected,
  isActive,
  onSelect,
  onNavigate,
  surface = "dark"
}: Props) {
  const {
    title,
    description
  } = extractTitleAndDescription(category.title);
  const isLight = surface === "light";
  const commonStyles = [{
    "display": "flex",
    "height": "8rem",
    "width": "100%",
    "alignItems": "center",
    "justifyContent": "space-between",
    "paddingLeft": "2.4rem",
    "paddingRight": "2.4rem",
    "textAlign": "left"
  }, {
    "fontSize": "1.6rem",
    "lineHeight": "1.5",
    "fontWeight": "400"
  }, isSelected && {
    "fontWeight": "700"
  }];
  const contentNode = <>
      <_StyledDiv>
        <_StyledDiv2>
          <_StyledH data-split-text $_css2={[{
          "fontSize": "1.8rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.018rem",
          "fontWeight": "400"
        }, isLight ? {
          "--tw-text-opacity": "1",
          "color": "rgb(0 7 4 / var(--tw-text-opacity))"
        } : {
          "color": "inherit"
        }]}>
            {title}
          </_StyledH>
        </_StyledDiv2>
        {description && <_StyledDiv3>
            <_StyledP data-split-text $_css3={[paragraph, {
          "overflow": "hidden",
          "textOverflow": "ellipsis",
          "whiteSpace": "nowrap",
          "fontSize": "1.4rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.014000000000000002rem",
          "fontWeight": "400"
        }, isLight ? {
          "--tw-text-opacity": "1",
          "color": "rgb(0 7 4 / var(--tw-text-opacity))",
          "opacity": "0.5"
        } : {
          "--tw-text-opacity": "1",
          "color": "rgb(255 255 255 / var(--tw-text-opacity))",
          "opacity": "0.5"
        }]}>
              {description}
            </_StyledP>
          </_StyledDiv3>}
      </_StyledDiv>
      <_StyledDiv4>
        {category.type === "multi-level" && <Icon icon={CaretRight} size="16" />}
        {category.type === "internal" && <Icon icon={ArrowRight} size="16" />}
        {category.type === "external" && <Icon icon={ArrowTopRight} size="16" />}
      </_StyledDiv4>
    </>;
  if (category.type === "multi-level") {
    return <_StyledButton data-category-text onClick={() => onSelect(category.id)} $_css4={[...commonStyles, isLight && {
      "--tw-text-opacity": "1",
      "color": "rgb(0 7 4 / var(--tw-text-opacity))"
    }]}>
        {contentNode}
      </_StyledButton>;
  }
  return <_StyledLink data-category-text href={category.url || "#"} target={category.type === "external" ? "_blank" : undefined} rel={category.type === "external" ? "noopener noreferrer" : undefined} onClick={onNavigate} $_css5={[...commonStyles, {
    "textDecorationLine": "none"
  }, isLight ? {
    "--tw-text-opacity": "1",
    "color": "rgb(0 7 4 / var(--tw-text-opacity))"
  } : {
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  }]}>
      {contentNode}
    </_StyledLink>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-12qlcpj-0"
})({
  "maxWidth": "calc(100% - 4.6rem)"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-12qlcpj-1"
})({
  "overflow": "hidden"
});
var _StyledH = _styled3("h4").withConfig({
  componentId: "sc-sf-12qlcpj-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-12qlcpj-3"
})({
  "marginTop": "0.4rem",
  "overflow": "hidden"
});
var _StyledP = _styled5("p").withConfig({
  componentId: "sc-sf-12qlcpj-4"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-12qlcpj-5"
})({
  "marginLeft": "1.6rem",
  "marginTop": "0.3rem",
  "minWidth": "1.6rem"
});
var _StyledButton = _styled7("button").withConfig({
  componentId: "sc-sf-12qlcpj-6"
})`${p => p.$_css4}`;
var _StyledLink = /*#__PURE__*/_styled8(Link).withConfig({
  displayName: "category-link___StyledLink",
  componentId: "sc-1ng4d04-0"
})(["", ""], p => p.$_css5);